import { useMutation, UseMutationResult, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { 
  ServiceStatusResponse, 
  NextServiceStatusesResponse,
  AvailableStatusesForServiceTypes,
  StatusChangeData,
  ServiceType,
} from '../@types/service.type';
import { 
  getNextServiceStatuses,
  saveServiceStatus,
  getServiceCancelOrReopenStatuses
} from '../api/services';
import { 
  createServiceStatus, 
  getServiceStatusByCode, 
  getAllServiceStatus, 
  updateServiceStatus,
  getPossibleStatusesByServiceTypes,
} from '../api/serviceStatuses';
import { AvailableStatusKey, CancelReOpenStatusKey, NextServiceStatusKey, ServiceStatusCodeKey, ServiceStatusKey } from '../configs/service-status';
import { ServiceTypeKey, ServiceTypeStatusOptionKey } from '../configs/service-type';
import {
  ServiceLogAllKey,
  CheckInKey,
  GetServiceKey
} from '../configs/services';
import { PartActionKey } from '../configs/partAction';

export const useGetServiceStatuses = (): UseQueryResult<ServiceStatusResponse[]> => {
  return useQuery({
    queryKey: [ServiceStatusKey],
    queryFn: () => getAllServiceStatus(),
    select: (response: any) => response
  });
};

export const useGetNextServicesStatuses = (code: string, serviceType: string, serviceId: number): UseQueryResult<NextServiceStatusesResponse> => {
  return useQuery({
    queryKey: [NextServiceStatusKey, code],
    queryFn: () => getNextServiceStatuses(code, serviceType, serviceId),
    enabled: false
  });
};

export const useGetServicesStatuses = (): UseQueryResult<ServiceStatusResponse[]> => {
  return useQuery({
    queryKey: [ServiceStatusKey],
    queryFn: () => getAllServiceStatus(),
    enabled: false
  });
};

export const useGetServiceStatusByCode = (code: string): UseQueryResult<ServiceStatusResponse> => {
  return useQuery({
    queryKey: [ServiceStatusKey, code],
    queryFn: () => code && getServiceStatusByCode(code),
    select: (response: any) => response,
    enabled: false
  });
};

export const useGetServiceStatusByShortName = (code: string): UseQueryResult<ServiceStatusResponse> => {
  return useQuery({
    queryKey: [ServiceStatusCodeKey, code],
    queryFn: () => getServiceStatusByCode(code),
    select: (response: any) => response,
    retry: false,
    enabled: false
  });
};

export const useCreateServiceStatus = (code: string): UseMutationResult => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: any) => {
      return await createServiceStatus(data);
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [ServiceStatusKey]
      });
      await queryClient.invalidateQueries({
        queryKey: [ServiceStatusKey, code]
      });
    }
  });
};

export const useUpdateServiceStatus = (code: string): UseMutationResult => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: any) => {
      return await updateServiceStatus(data.code, data);
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [ServiceStatusKey]
      });
      await queryClient.invalidateQueries({
        queryKey: [ServiceStatusKey, code]
      });
    }
  });
};

export const useSaveServiceStatus = (id: number): UseMutationResult => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: any) => {
      return await saveServiceStatus(id, data);
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [ServiceTypeKey, id]
      });
      await queryClient.invalidateQueries({
        queryKey: [ServiceLogAllKey, id]
      });
      await queryClient.invalidateQueries({
        queryKey: [GetServiceKey, id]
      });
      await queryClient.invalidateQueries({
        queryKey: [CheckInKey, id]
      });
      await queryClient.invalidateQueries({
        queryKey: [PartActionKey, id]
      });
    }
  });
};

export const useGetPossibleStatusesByServiceTypes = (data: StatusChangeData): UseQueryResult<AvailableStatusesForServiceTypes> => {
  return useQuery({
    queryKey: [AvailableStatusKey, data?.serviceTypes],
    queryFn: () => getPossibleStatusesByServiceTypes(data.code, data.serviceTypes),
    enabled: false,
    select: (response: any) => response
  });
};

export const useGetServiceCancelOrReopenStatuses = (option: string, statusCode: string, serviceType: string, serviceId: number): UseQueryResult<ServiceType[]> => {
  return useQuery({
    queryKey: [CancelReOpenStatusKey],
    queryFn: () => option && getServiceCancelOrReopenStatuses(option, statusCode, serviceType, serviceId),
    enabled: false
  });
};

export const useGetAllServiceStatus = (): UseQueryResult<[ServiceStatusResponse]> => {
  return useQuery({
    queryKey: [ServiceTypeStatusOptionKey],
    queryFn: () => getAllServiceStatus()
  });
};